/**
 * useEmailSettings Hook
 *
 * Get and update account settings via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, EmailAccountSettings, EmailAccountSettingsInput } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { errorMessage } from '../types';

export function useEmailSettings(accountId: string | undefined) {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: [...queryKeys.email.all, 'settings', accountId],
    queryFn: async () => {
      if (!accountId) throw new Error('No account selected');
      return api.getEmailAccountSettings(accountId);
    },
    enabled: !!accountId,
  });

  const updateSettings = useCallback(
    async (input: Partial<EmailAccountSettingsInput>) => {
      if (!accountId) return;
      const result = await api.updateEmailAccountSettings(accountId, input);
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.email.all, 'settings', accountId],
      });
      return result;
    },
    [accountId, queryClient],
  );

  return {
    settings: queryResult.data ?? (null as EmailAccountSettings | null),
    loading: queryResult.isLoading,
    error: errorMessage(queryResult.error),
    refresh: queryResult.refetch,
    updateSettings,
  };
}
